import { ReactElement, useState, useEffect } from "react";
import { Button, Col, Drawer, Form, Input, Row, Select, Upload, message, Checkbox } from "antd";
import slugify from "react-slugify";
import { InboxOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import firebase from "firebase/app";
import { DrawerFooter } from "../../components/GlobalStyles";
import { CREATE_CLUB_REQUEST } from "../../redux/constants";
import { getUserState } from "../../redux/selectors";
import { countries, clubStatus, currencies } from "../../constants";

const { useForm } = Form;
const { Option } = Select;

type Props = {
  visible: boolean;
  onClose: () => void;
};

function beforeUpload(file: any) {
  const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
  if (!isJpgOrPng) {
    message.error("You can only upload JPG/PNG file!");
  }
  return false;
}

export function CreateClubDrawer({ visible, onClose }: Props): ReactElement {
  const dispatch = useDispatch();
  const { users } = useSelector(getUserState);
  const [form] = useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [currency, setCurrency] = useState<string>("AED");
  const [managers, setManagers] = useState<any[]>([]);
  const [price30, setPrice30] = useState<string>("0");
  const [price60, setPrice60] = useState<string>("0");
  const [price90, setPrice90] = useState<string>("0");
  const [price120, setPrice120] = useState<string>("0");
  const [checked, setChecked] = useState<boolean>(false);
  const { t } = useTranslation();
  const storage = firebase.storage();

  useEffect(() => {
    const mManagers = users.filter((user) => user.role === "manager" && user.club === "");
    setManagers(mManagers);
  }, []);

  const getClubId = async () => {
    const lastClubsSnapshot = await firebase.firestore().collection("clubs").orderBy('clubId', "desc").limit(1).get();
    
    if(lastClubsSnapshot.size === 0) {
      return "001";
    }
    const { clubId } = lastClubsSnapshot.docs[0].data();
    const newClubId = parseInt(clubId, 10) + 1;
    if(newClubId < 10 ) return `00${newClubId}`;
    if(newClubId > 9 && newClubId < 100) return `0${newClubId}`;
    return `${newClubId}`;
  }

  const onFinish = async (values: any) => {
    const newClubId = await getClubId();
    if(newClubId === "NaN"){
      message.error("Something went wrong!");
      return;
    }
    setLoading(true);
    const formValue = {
      ...values,
      clubId: newClubId,
      firestoreRef: newClubId,
      currency,
      price: {
        30: price30,
        60: price60,
        90: price90,
        120: price120,
      },
      // enable : checked,
      bookingDatesRange: values.bookingDatesRange === "" || values.bookingDatesRange === 0 ? 2 : values.bookingDatesRange,
      payOverCount: values.payOverCount === "pay_over_count",
      manager: values.manager === undefined ? [] : values.manager
    };
    if (values.photo) {
      const ref = `clubs/${values.name}/${values.photo.file.name}`;
      await storage.ref(ref).put(values.photo.file);
      const clubImageUrl = await storage.ref(ref).getDownloadURL();
      formValue.photo = clubImageUrl;
    } else {
      formValue.photo = "";
    }
    Object.keys(formValue).forEach((key) => {
      if (formValue[key] === undefined) formValue[key] = "";
    });

    dispatch({ type: CREATE_CLUB_REQUEST, payload: formValue });
    form.resetFields();
    setLoading(false);
    onClose();
  };

  const toggleCheck = () => {
    setChecked(!checked);
  }

  const onFinishFailed = (errorInfo: any) => {
    // eslint-disable-next-line no-console
    console.log("Failed:", errorInfo);
  };

  const onChangePriceCurreny = (value: string) => {
    setCurrency(value);
  };

  return (
    <Drawer
      title={t("club.create_club")}
      width={720}
      onClose={onClose}
      visible={visible}
      bodyStyle={{ paddingBottom: 80 }}
    >
      <Form 
        form={form} 
        layout="vertical" 
        onFinish={onFinish} 
        onFinishFailed={onFinishFailed}
        initialValues={{
          name : "",
          manager: [],
          country: "ae",
          currency: "AED",
          status: "coming_soon",
          show: "show",
          bookingDatesRange: 10,
          payOverCount: "pay_over_count"
        }}
      >
        {/* Club name and manager */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label={t("club.club_name")}
              rules={[
                {
                  required: true,
                  message: t("club.club_name_placeholder"),
                },
              ]}
            >
              <Input placeholder={t("club.club_name_placeholder")} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="manager" label={t("club.club_manager")} >
              <Select placeholder={t("club.club_manager_placeholder")} mode="multiple" allowClear>
                {managers.map((manager) => {
                  return (
                    <Option key={manager.id} value={manager.id}>
                      {manager.fullName}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        {/* Photo */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="photo" label="Photo">
              <Upload.Dragger
                name="files"
                beforeUpload={beforeUpload}
                showUploadList
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag file to this area to upload
                </p>
                <p className="ant-upload-hint"> Support for a single upload.</p>
              </Upload.Dragger>
            </Form.Item>
          </Col>
        </Row>
        {/* Country */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="country" label={t("club.location")} >
              <Select placeholder={t("club.location_placeholder")}>
                { countries.map(({code, country}) => (
                  <Option key={code} value={code}>{country}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="currency" label={t("club.currency")}>
              <Select
                className="select-before"
                onChange={onChangePriceCurreny}
              >
                {currencies.map( c => <Option value={c.value}>{c.label}</Option>)}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        {/* Prices */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label={t("club.price")}>
              <Input
                style={{ width: "50%" }}
                placeholder={t("club.price_placeholder")}
                addonBefore={<span>30 Mins</span>}
                onChange={(event) => setPrice30(event.target.value)}
                type="number"
                required
              />
              <Input
                style={{ width: "50%" }}
                placeholder={t("club.price_placeholder")}
                addonBefore={<span>60 Mins</span>}
                onChange={(event) => setPrice60(event.target.value)}
                type="number"
                required
              />
              <Input
                style={{ width: "50%", marginTop : 10 }}
                placeholder={t("club.price_placeholder")}
                addonBefore={<span>90 Mins</span>}
                onChange={(event) => setPrice90(event.target.value)}
                type="number"
                required
              />
              <Input
                style={{ width: "50%", marginTop : 10 }}
                placeholder={t("club.price_placeholder")}
                addonBefore={<span>120 Mins</span>}
                onChange={(event) => setPrice120(event.target.value)}
                type="number"
                required
              />
            </Form.Item>
              
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="payOverCount" label={t("club.payment_method")}>
              <Select placeholder={t("club.payment_method_placeholder")}>
                <Option value="pay_over_count">Pay Over Count</Option>
                <Option value="online">Online</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        {/* Website */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="website"
              label={t("club.website")}
              rules={[
                {
                  required: true,
                  message: t("club.website_placeholder"),
                },
              ]}
            >
              <Input
                addonBefore="https://"
                style={{ width: "100%" }}
                placeholder={t("club.website_placeholder")}
              />
            </Form.Item>
          </Col>
        </Row>
        {/* Contact information */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="contactEmail"
              label={t("club.contact_email")}
              rules={[
                {
                  type: "email",
                  message: "Please enter valid email address",
                },
                {
                  required: true,
                  message: "Please enter valid email address"
                }
              ]}
            >
              <Input placeholder={t("club.contact_email")} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item 
              name="contactPhone" 
              label={t("club.contact_number")}
              rules={[
                {
                  required: true,
                  message: "Please enter contact phone number"
                }
              ]}
            >
              <Input placeholder={t("club.contact_number")} />
            </Form.Item>
          </Col>
        </Row>
        {/* Social Liknks */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="facebook" label={t("club.facebook")}>
              <Input placeholder={t("club.facebook")} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="instagram" label={t("club.instagram")}>
              <Input placeholder={t("club.instagram")} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="linkedIn" label={t("club.linkedIn")}>
              <Input placeholder={t("club.linkedIn")} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="twitter" label={t("club.twitter")}>
              <Input placeholder={t("club.twitter")} />
            </Form.Item>
          </Col>
        </Row>
        {/* Club Status */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="status" label="Club Status">
              <Select placeholder="Please select club status">
                {clubStatus.map(({status, label }) => (
                  <Option key={status} value={status}>{label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="show" label="Hide/Show">
              <Select placeholder="Please select club status">
                <Option value="show">Show</Option>
                <Option value="hide">Hide</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              name="bookingDatesRange" 
              label="Booking Date Range"
            >
              <Input placeholder="Please enter the number of dates" type="number" min={2} max={30} />
            </Form.Item>
          </Col>
        </Row>

        {/* Club Description */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="description"
              label={t("club.description")}
              rules={[
                {
                  required: true,
                  message: t("club.description_placeholder"),
                },
              ]}
            >
              <Input.TextArea
                rows={4}
                placeholder={t("club.description_placeholder")}
              />
            </Form.Item>
          </Col>
        </Row>

        <DrawerFooter>
          <Button onClick={onClose} style={{ marginRight: 8 }}>
            {t("common.cancel")}
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {t("common.submit")}
          </Button>
        </DrawerFooter>
      </Form>
    </Drawer>
  );
}
